import React from 'react';
import styled, { keyframes } from 'styled-components';
import ScrollReveal from './shared/ScrollReveal';
var locationData = require('./locationData');

const Section = styled.section`
  padding: 96px 24px;
  max-width: 1120px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 64px 16px;
  }
`;

const Header = styled.div`
  margin-bottom: 56px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.accent};
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  font-weight: 600;
  color: ${p => p.theme.text};
  letter-spacing: -0.03em;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const ExampleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  max-width: 960px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 640px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: ${p => p.offset ? '64px' : '0'};

  @media (max-width: 768px) {
    padding-top: 0;
  }
`;

const ExchangeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const QueryCard = styled.div`
  background: var(--tz-wash, rgba(255, 255, 255, 0.04));
  border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.06));
  border-bottom: none;
  border-radius: 14px 14px 0 0;
  padding: 18px 22px;
`;

const ResponseCard = styled.div`
  background: var(--tz-wash, rgba(255, 255, 255, 0.02));
  border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.06));
  border-top: 1px solid var(--tz-wash, rgba(255, 255, 255, 0.03));
  border-radius: 0 0 14px 14px;
  padding: 22px 22px 24px;
`;

const QueryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const UserAvatar = styled(Avatar)`
  background: var(--tz-hairline, rgba(255, 255, 255, 0.08));
  color: ${p => p.theme.textTertiary};
`;

const BotAvatar = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
`;

const QueryText = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: ${p => p.theme.text};
`;

const ResponseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const BotName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.theme.textTertiary};
`;

const ToolCall = styled.div`
  font-family: ${p => p.theme.fontMono};
  font-size: 12px;
  color: ${p => p.theme.textTertiary};
  background: var(--tz-wash, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--tz-wash, rgba(255, 255, 255, 0.04));
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ToolIcon = styled.span`
  color: ${p => p.theme.accent};
  font-size: 11px;
`;

const ResponseText = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: ${p => p.theme.textSecondary};
`;

const DataPoint = styled.span`
  color: ${p => p.theme.text};
  font-weight: 500;
`;

const Highlight = styled.span`
  color: ${p => p.theme.accent};
  font-weight: 500;
`;

const MiniTable = styled.div`
  margin-top: 12px;
  font-family: ${p => p.theme.fontMono};
  font-size: 13px;
  line-height: 1.8;
  color: ${p => p.theme.textTertiary};
`;

const MiniRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
  border-bottom: 1px solid var(--tz-wash, rgba(255, 255, 255, 0.03));

  &:last-child {
    border-bottom: none;
  }
`;

const MiniLabel = styled.span`
  color: ${p => p.theme.textTertiary};
`;

const MiniValue = styled.span`
  color: ${p => p.theme.textSecondary};
`;

/* Animation primitives */

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
  40% { transform: scale(1); opacity: 1; }
`;

const cursorBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const FadeSlide = styled.div`
  opacity: ${p => p.show ? 1 : 0};
  transform: translateY(${p => p.show ? '0' : '12px'});
  transition: opacity 0.4s ease, transform 0.4s ease;
`;

const ThinkingFade = styled.div`
  overflow: hidden;
  max-height: ${p => p.show ? '30px' : '0'};
  opacity: ${p => p.show ? 1 : 0};
  transition: max-height 0.3s ease, opacity 0.2s ease;
`;

const ThinkingWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 0;
`;

const ThinkingDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${p => p.theme.textTertiary};
  animation: ${dotBounce} 1.4s ease-in-out infinite;
  animation-delay: ${p => p.d};
`;

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1.1em;
  background: ${p => p.theme.accent};
  margin-left: 1px;
  vertical-align: text-bottom;
  animation: ${cursorBlink} 0.8s step-end infinite;
`;

/* Data — segments format: string | ["style", "text"] */

var EXAMPLES = [
  {
    query: "How much did I spend charging last month?",
    tool: "get_charge_report",
    segments: [
      "In ", ["data", "February 2026"], ", you spent a total of ",
      ["highlight", "$42.18"], " across ", ["data", "12 charging sessions"],
      ", adding 384.2 kWh to your Model 3.",
    ],
    table: [
      { label: "Home charging", value: "9 sessions \u00b7 $28.44" },
      { label: "Supercharger", value: "2 sessions \u00b7 $11.20" },
      { label: "Other DC fast", value: "1 session \u00b7 $2.54" },
    ],
  },
  {
    query: "What\u2019s my battery health looking like?",
    tool: "get_battery_health",
    segments: [
      "Your Model 3\u2019s battery is in ", ["highlight", "great shape"],
      ". After ", ["data", "287 full-cycle equivalents"],
      ", you\u2019ve lost about ", ["data", "4.2%"],
      " of original capacity \u2014 that puts your estimated range at ",
      ["data", "301 miles"], " vs.\u00a0the original 315. You\u2019re tracking well ahead of the TezLab fleet average for vehicles at this mileage.",
    ],
  },
];

/* Examples 3 & 4 are built dynamically from browser locale — see locationData.js */

/* Components */

function ThinkingDots() {
  return (
    <ThinkingWrap>
      <ThinkingDot d="0s" />
      <ThinkingDot d="0.2s" />
      <ThinkingDot d="0.4s" />
    </ThinkingWrap>
  );
}

function TypedResponse(props) {
  var segments = props.segments;
  var table = props.table;
  var active = props.active;
  var reducedMotion = props.reducedMotion;

  var totalCharsRef = React.useRef(0);
  var charCountRef = React.useRef(0);
  var rafRef = React.useRef(null);
  var lastTimeRef = React.useRef(0);
  var mountedRef = React.useRef(true);

  var forceUpdate = React.useState(0)[1];
  var showTableState = React.useState(false);
  var showTable = showTableState[0];
  var setShowTable = showTableState[1];

  React.useEffect(function () {
    var total = 0;
    for (var i = 0; i < segments.length; i++) {
      var seg = segments[i];
      total += (typeof seg === 'string' ? seg : seg[1]).length;
    }
    totalCharsRef.current = total;

    if (reducedMotion) {
      charCountRef.current = total;
      setShowTable(!!table);
    }

    return function () { mountedRef.current = false; };
  }, []);

  React.useEffect(function () {
    if (!active || reducedMotion) return;
    if (charCountRef.current >= totalCharsRef.current) return;

    function tick(time) {
      if (!mountedRef.current) return;
      if (!lastTimeRef.current) lastTimeRef.current = time;

      if (time - lastTimeRef.current >= 18) {
        charCountRef.current = Math.min(charCountRef.current + 2, totalCharsRef.current);
        lastTimeRef.current = time;
        forceUpdate(function (n) { return n + 1; });

        if (charCountRef.current >= totalCharsRef.current) {
          if (table) {
            setTimeout(function () {
              if (mountedRef.current) setShowTable(true);
            }, 200);
          }
          return;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return function () {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  if (!active && !reducedMotion) return null;

  var rendered = [];
  var remaining = charCountRef.current;

  for (var i = 0; i < segments.length; i++) {
    if (remaining <= 0) break;
    var seg = segments[i];
    var text = typeof seg === 'string' ? seg : seg[1];
    var style = typeof seg === 'string' ? null : seg[0];
    var visible = text.substring(0, remaining);
    remaining -= text.length;

    if (style === 'data') {
      rendered.push(React.createElement(DataPoint, { key: i }, visible));
    } else if (style === 'highlight') {
      rendered.push(React.createElement(Highlight, { key: i }, visible));
    } else {
      rendered.push(React.createElement(React.Fragment, { key: i }, visible));
    }
  }

  var done = charCountRef.current >= totalCharsRef.current;

  return (
    <ResponseText>
      {rendered}
      {!done && <Cursor />}
      {table && (
        <MiniTable style={{ opacity: showTable ? 1 : 0, transition: 'opacity 0.4s ease' }}>
          {table.map(function (row, j) {
            return (
              <MiniRow key={j}>
                <MiniLabel>{row.label}</MiniLabel>
                <MiniValue>{row.value}</MiniValue>
              </MiniRow>
            );
          })}
        </MiniTable>
      )}
    </ResponseText>
  );
}

function AnimatedExchange(props) {
  var query = props.query;
  var tool = props.tool;
  var segments = props.segments;
  var table = props.table;
  var delay = props.delay;
  var sectionVisible = props.sectionVisible;
  var reducedMotion = props.reducedMotion;

  var phaseState = React.useState(reducedMotion && sectionVisible ? 4 : 0);
  var phase = phaseState[0];
  var setPhase = phaseState[1];
  var timersRef = React.useRef([]);

  React.useEffect(function () {
    if (!sectionVisible) return;

    if (reducedMotion) {
      setPhase(4);
      return;
    }

    timersRef.current.push(setTimeout(function () { setPhase(1); }, delay));
    timersRef.current.push(setTimeout(function () { setPhase(2); }, delay + 400));
    timersRef.current.push(setTimeout(function () { setPhase(3); }, delay + 1100));
    timersRef.current.push(setTimeout(function () { setPhase(4); }, delay + 1700));

    return function () {
      timersRef.current.forEach(clearTimeout);
    };
  }, [sectionVisible]);

  return (
    <ExchangeGroup>
      <FadeSlide show={phase >= 1}>
        <QueryCard>
          <QueryRow>
            <UserAvatar>Y</UserAvatar>
            <QueryText>{query}</QueryText>
          </QueryRow>
        </QueryCard>
      </FadeSlide>
      <FadeSlide show={phase >= 2}>
        <ResponseCard>
          <ResponseHeader>
            <BotAvatar><TezLabIcon /></BotAvatar>
            <BotName>TezLab MCP</BotName>
          </ResponseHeader>
          <FadeSlide show={phase >= 3}>
            <ToolCall>
              <ToolIcon>&#x25B6;</ToolIcon>
              {tool}()
            </ToolCall>
          </FadeSlide>
          <ThinkingFade show={phase >= 2 && phase < 4}>
            <ThinkingDots />
          </ThinkingFade>
          <TypedResponse
            segments={segments}
            table={table}
            active={phase >= 4}
            reducedMotion={reducedMotion}
          />
        </ResponseCard>
      </FadeSlide>
    </ExchangeGroup>
  );
}

function TezLabIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M0 4C0 1.79086 1.79086 0 4 0H36C38.2091 0 40 1.79086 40 4V36C40 38.2091 38.2091 40 36 40H4C1.79086 40 0 38.2091 0 36V4ZM15 22V25.625C15 25.8321 15.1679 26 15.375 26H18.125C18.3321 26 18.5 25.8321 18.5 25.625V22V18.5V14.375C18.5 14.1679 18.3321 14 18.125 14H15.375C15.1679 14 15 14.1679 15 14.375V18.5H8.375C8.16789 18.5 8 18.6679 8 18.875V21.625C8 21.8321 8.16789 22 8.375 22H15ZM21.5 22.5V14.375C21.5 14.1679 21.6679 14 21.875 14H24.625C24.8321 14 25 14.1679 25 14.375V22.5C25 22.5 30.9843 22.5 31.625 22.5C31.8321 22.5 32 22.6679 32 22.875V25.625C32 25.8321 31.8321 26 31.625 26H25H21.875H21.75C21.6119 26 21.5 25.8881 21.5 25.75V25.625V22.5Z" fill="white"/>
    </svg>
  );
}

export default function ConversationExamples() {
  var visibleState = React.useState(false);
  var visible = visibleState[0];
  var setVisible = visibleState[1];
  var gridRef = React.useRef(null);
  var reducedMotion = React.useRef(false);

  /* Build full examples list once on mount — static examples + locale-aware dynamic ones */
  var allExamplesRef = React.useRef(null);
  if (!allExamplesRef.current) {
    var loc = locationData.pickLocationGroup();
    var dynamic = locationData.buildLocationExamples(loc);
    allExamplesRef.current = EXAMPLES.concat(dynamic);
  }
  var allExamples = allExamplesRef.current;

  React.useEffect(function () {
    reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var el = gridRef.current;
    if (!el) return;
    var observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return function () { observer.disconnect(); };
  }, []);

  /* Stagger: zig-zag across columns (top-L, top-R, bottom-L, bottom-R) */
  var delays = [0, 1200, 600, 1800];

  return (
    <Section>
      <ScrollReveal>
        <Header>
          <SectionLabel>Examples</SectionLabel>
          <SectionTitle>See what you can ask</SectionTitle>
        </Header>
      </ScrollReveal>
      <ExampleGrid ref={gridRef}>
        <Column>
          {[0, 1].map(function (idx) {
            var ex = allExamples[idx];
            return (
              <AnimatedExchange
                key={idx}
                query={ex.query}
                tool={ex.tool}
                segments={ex.segments}
                table={ex.table}
                delay={delays[idx]}
                sectionVisible={visible}
                reducedMotion={reducedMotion.current}
              />
            );
          })}
        </Column>
        <Column offset>
          {[2, 3].map(function (idx) {
            var ex = allExamples[idx];
            return (
              <AnimatedExchange
                key={idx}
                query={ex.query}
                tool={ex.tool}
                segments={ex.segments}
                table={ex.table}
                delay={delays[idx]}
                sectionVisible={visible}
                reducedMotion={reducedMotion.current}
              />
            );
          })}
        </Column>
      </ExampleGrid>
    </Section>
  );
}
